import { dropDraftImages } from './imageDraftStore';

/**
 * Forget a use case's retained inputs once its study has actually run.
 *
 * The wizard keeps each form's editable state in `formState[useCaseType.toLowerCase()]`
 * so pressing Back to change the audience doesn't blank it. Nothing ever removed that
 * entry, and the images it points at had no delete either — so a completed study's name,
 * brand, goal and photos were still there when the user picked the same use case again,
 * and Run submitted the old study's images as the new one (#376).
 *
 * Called when the NEXT study of the use case starts, not when one is submitted — see
 * `markUseCaseSubmitted` for why.
 */
export function discardSubmittedUseCaseDraft<T extends Record<string, unknown>>(
  formState: Record<string, T>,
  useCaseType: string | null | undefined,
): Record<string, T> {
  if (!useCaseType) return formState;

  const key = useCaseType.toLowerCase();
  const draft = formState[key];
  if (!draft) return formState;

  dropDraftImages(draft.imageDraftKey as string | undefined);

  const next = { ...formState };
  delete next[key];
  return next;
}

/**
 * Record that a study of this use case has been submitted.
 *
 * Run only QUEUES the study: `ReviewAndRun` calls `onComplete` the moment
 * `createSimulation` returns, while the run is still going and may still fail. So submit is
 * the wrong moment to throw the inputs away, for two reasons that both bite:
 *
 *  - the results screen's "Back to review" has a pencil-Edit beside the study inputs it
 *    displays, and that form reads `formState` — discarding at submit opened it BLANK, with
 *    the photos unrecoverable, next to a card still showing the values;
 *  - a study that fails after queueing has to keep its inputs. Losing them on a failure is
 *    the complaint this whole spec exists to fix.
 *
 * So the use case is only MARKED here. `startUseCase` does the discarding, which still
 * closes #376 — the next study of the same use case opens empty.
 */
export function markUseCaseSubmitted(
  submitted: ReadonlySet<string>,
  useCaseType: string | null | undefined,
): Set<string> {
  const next = new Set(submitted);
  if (useCaseType) next.add(useCaseType.toLowerCase());
  return next;
}

/**
 * Begin a study of `useCaseType`, discarding a previously-submitted draft for it.
 *
 * Returns both new values so the caller has no ordering to get wrong, and so the decision
 * is testable without reaching through six `setState` calls.
 */
export function startUseCase<T extends Record<string, unknown>>(
  formState: Record<string, T>,
  submitted: ReadonlySet<string>,
  useCaseType: string | null | undefined,
): { formState: Record<string, T>; submitted: Set<string> } {
  const key = useCaseType?.toLowerCase();
  if (!key || !submitted.has(key)) return { formState, submitted: new Set(submitted) };

  const remaining = new Set(submitted);
  remaining.delete(key);
  return {
    formState: discardSubmittedUseCaseDraft(formState, useCaseType),
    submitted: remaining,
  };
}
